"use client";

import { useEffect, useState } from "react";
import { GoogleTagManager } from "@next/third-parties/google";

const IDLE_TIMEOUT_MS = 3_000;

/**
 * Defers Google Tag Manager to a browser idle period (after load/LCP settle),
 * capped at 3s. Loading in idle — rather than on the user's first interaction —
 * keeps GTM's tag cascade off the main thread during interactions (INP).
 *
 * GTM's dataLayer queue replays events pushed before it loads, so no data is lost.
 */
export function DeferredGTM({ gtmId }: { gtmId: string }) {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const load = () => setShouldLoad(true);

    if ("requestIdleCallback" in window) {
      const handle = window.requestIdleCallback(load, {
        timeout: IDLE_TIMEOUT_MS,
      });
      return () => window.cancelIdleCallback(handle);
    }

    // Safari lacks requestIdleCallback — approximate with a short timeout.
    const timer = setTimeout(load, IDLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!shouldLoad) return null;

  return <GoogleTagManager gtmId={gtmId} />;
}
